import { useState } from 'react'
import { ChevronDown, ChevronUp, Plus, Signpost, Trash2, Undo2 } from 'lucide-react'

import { Field } from '../../components/Field'
import { TulipSketch } from '../../components/TulipSketch'
import {
  directionLabel,
  emptyTulip,
  exitDirection,
  TULIP_SHAPES,
  TULPEN_NOTIZ_ZEICHEN_MAX,
  tulipShapeInfo,
  tulipText,
} from '../../lib/tulip'
import { mitAbsage, ohneAbsage, stelleAus } from '../../lib/tulipMerge'
import type { Stage, Tulip, TulipShape } from '../../lib/types'
import { useRowKeys } from './rowKeys'
import { appendItem, insertAt, moveItem, patchAt, removeAt } from './stageLists'

/**
 * Die Tulpen einer Etappe eintragen, ändern, verschieben und löschen.
 *
 * Der Grund, dass es diese Maske gibt: die Reise weicht von der Planung ab. Eine
 * Notation, die nur zu Hause am grossen Bildschirm befüllt werden kann, ist auf
 * der Fahrt wertlos - die Kehre, die im Buch fehlt, ist immer die, von der
 * vorher niemand wusste.
 *
 * Bedient wird derselbe Weg wie bei den Schlafplätzen und Highlights: dieselben
 * Listenoperationen (stageLists.ts), dieselbe Schlüsselliste für die
 * React-Schlüssel (rowKeys.ts), dasselbe Zurückholen des zuletzt Entfernten.
 * Gelöscht wird ohne Rückfrage - eine Rückfrage bei jedem Handgriff wäre am
 * Strassenrand nur lästig -, dafür kostet ein Fehlgriff nichts.
 *
 * Neben jeder Zeile steht die Skizze in genau der Grösse, in der sie im Buch
 * gedruckt wird. Nicht grösser: wer hier eine Form wählt, soll sehen, was er
 * im Auto sehen wird, und nicht eine schöne Zeichnung, die auf Papier zu Matsch
 * wird.
 *
 * Das Löschen einer übernommenen Tulpe ist zugleich ein Nein zu dem Vorschlag,
 * aus dem sie kam (Stage.tulipsRejected, siehe lib/tulipMerge.ts). Sonst wäre
 * sie beim nächsten Druck auf „Tulpen vorschlagen“ wieder da, angehakt, und
 * käme irgendwann versehentlich ein zweites Mal ins Buch. Das Zurückholen nimmt
 * das Nein wieder mit weg - und eine selbst geschriebene Zeile hinterlässt beim
 * Löschen gar nichts, denn vorgeschlagen war sie nie.
 */
export function TulipEditor({
  stage,
  onChange,
}: {
  stage: Stage
  onChange: (patch: Partial<Stage>) => void
}) {
  const werte = stage.tulips ?? []
  const keys = useRowKeys(werte.length)
  const [zurueck, setZurueck] = useState<{ index: number; tulpe: Tulip } | null>(null)

  function entfernen(index: number) {
    const tulpe = werte[index]
    if (!tulpe) return

    keys.remove(index)
    setZurueck({ index, tulpe })

    const stelle = herkunft(tulpe)
    onChange({
      tulips: removeAt(werte, index),
      ...(stelle ? { tulipsRejected: mitAbsage(stage.tulipsRejected ?? [], stelle) } : {}),
    })
  }

  function zurueckholen() {
    if (!zurueck) return

    keys.insert(zurueck.index)
    const stelle = herkunft(zurueck.tulpe)
    onChange({
      tulips: insertAt(werte, zurueck.index, zurueck.tulpe),
      ...(stelle ? { tulipsRejected: ohneAbsage(stage.tulipsRejected ?? [], stelle) } : {}),
    })
    setZurueck(null)
  }

  function anhaengen() {
    keys.append()
    // Nach dem Anhängen zeigt der gemerkte Index woandershin, also fallen
    // lassen. Dasselbe gilt beim Verschieben.
    setZurueck(null)
    onChange({ tulips: appendItem(werte, emptyTulip()) })
  }

  function verschieben(index: number, richtung: -1 | 1) {
    keys.move(index, richtung)
    setZurueck(null)
    onChange({ tulips: moveItem(werte, index, richtung) })
  }

  return (
    <section className="inhalt-block">
      <h5 className="block-titel">
        <Signpost size={13} /> Roadbook
      </h5>

      {werte.length === 0 && (
        <p className="leer">
          Noch keine Tulpe. Eine Tulpe ist eine Kreuzung als Skizze: der Punkt ist, wo du herkommst,
          der Pfeil, wo es weitergeht.
        </p>
      )}

      <ul className="eintragsliste">
        {werte.map((tulpe, i) => (
          <li key={keys.at(i)} className="eintrag tulpen-eintrag">
            <div className="tulpen-kopf">
              {/*
                Die Vorschau in Druckgrösse, daneben der Satz, den die
                Beifahrerin vorliest, wenn die Sonne auf dem Display steht.
              */}
              <TulipSketch tulip={tulpe} />
              <span>
                <span className="eintrag-nummer">Tulpe {i + 1}</span>
                <span className="tulpen-satz">{tulipText(tulpe)}</span>
              </span>
            </div>

            <div className="raster-2">
              <Field label="Kilometer ab Start">
                <input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step={0.1}
                  // Leer und nicht 0, solange niemand den Tacho abgelesen hat.
                  value={Number.isFinite(tulpe.km) ? tulpe.km : ''}
                  placeholder="offen"
                  onChange={(e) =>
                    onChange({
                      tulips: patchAt(werte, i, {
                        km: e.target.value === '' ? NaN : Number(e.target.value),
                      }),
                    })
                  }
                />
              </Field>

              <Field label="Form">
                <select
                  value={tulpe.shape}
                  onChange={(e) =>
                    onChange({ tulips: patchAt(werte, i, { shape: e.target.value as TulipShape }) })
                  }
                >
                  <optgroup label="Kreuzungen">
                    {TULIP_SHAPES.filter((info) => info.group === 'knoten').map((info) => (
                      <option key={info.key} value={info.key}>
                        {info.label}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="Unterwegs">
                    {TULIP_SHAPES.filter((info) => info.group === 'strecke').map((info) => (
                      <option key={info.key} value={info.key}>
                        {info.label}
                      </option>
                    ))}
                  </optgroup>
                </select>
              </Field>
            </div>

            <p className="tulpen-erklaerung">{tulipShapeInfo(tulpe.shape).hint}</p>

            {/*
              Die Richtung steht nur da, wo es eine zu wählen gibt. Ein Feld
              "Drehung" an einem Tunnel wäre eine Frage ohne Antwort - und im
              Stehen am Strassenrand eine, über die man nachdenkt.
            */}
            <Richtungswahl
              tulpe={tulpe}
              onChange={(turn) => onChange({ tulips: patchAt(werte, i, { turn }) })}
            />

            {/*
              Der Deckel auf dem Hinweis ist keine Sparsamkeit, sondern die
              Geometrie der gedruckten Reihe: neben dem Zeichen stehen fünf
              Zeilen zur Verfügung, und was darüber hinausging, machte die Reihe
              höher - bis die untersten Reihen einer vollen Roadbookseite unter
              die Kante fielen. Siehe TULPEN_NOTIZ_ZEICHEN_MAX in lib/tulip.ts.
              Der Satz darunter steht erst da, wenn er gebraucht wird: eine
              Zeichenzahl unter jedem leeren Feld wäre eine Belehrung.
            */}
            <Field label="Hinweis">
              <input
                value={tulpe.note}
                autoComplete="off"
                maxLength={TULPEN_NOTIZ_ZEICHEN_MAX}
                placeholder="z. B. 2. Ausfahrt, Schild Trenta"
                onChange={(e) => onChange({ tulips: patchAt(werte, i, { note: e.target.value }) })}
              />
            </Field>
            {tulpe.note.length >= TULPEN_NOTIZ_ZEICHEN_MAX && (
              <p className="tulpen-erklaerung">
                Mehr als {TULPEN_NOTIZ_ZEICHEN_MAX} Zeichen hat die Zeile im Buch nicht.
              </p>
            )}

            <div className="eintrag-knoepfe">
              <button
                type="button"
                className="knopf still"
                disabled={i === 0}
                onClick={() => verschieben(i, -1)}
                aria-label={`Tulpe ${i + 1} nach oben`}
              >
                <ChevronUp size={14} /> Früher
              </button>
              <button
                type="button"
                className="knopf still"
                disabled={i === werte.length - 1}
                onClick={() => verschieben(i, 1)}
                aria-label={`Tulpe ${i + 1} nach unten`}
              >
                <ChevronDown size={14} /> Später
              </button>
              <button
                type="button"
                className="knopf gefahr"
                onClick={() => entfernen(i)}
                aria-label={`Tulpe ${i + 1} entfernen`}
              >
                <Trash2 size={14} /> Entfernen
              </button>
            </div>
          </li>
        ))}
      </ul>

      <div className="knopfreihe">
        <button type="button" className="knopf" onClick={anhaengen}>
          <Plus size={14} /> Tulpe hinzufügen
        </button>
        {zurueck && (
          <button type="button" className="knopf still" onClick={zurueckholen}>
            <Undo2 size={14} /> {tulipText(zurueck.tulpe)} zurückholen
          </button>
        )}
      </div>
    </section>
  )
}

/**
 * Die Vorschlagsstelle, aus der diese Tulpe kam - oder nichts.
 *
 * Nichts bei jeder von Hand geschriebenen Zeile. Das ist Absicht: wer sie
 * löscht, hat keinen Vorschlag abgelehnt, sondern seine eigene Notiz
 * weggeworfen. Ein Nein daraus zu machen hiesse, künftig einen Vorschlag
 * stillzulegen, den nie jemand gesehen hat.
 */
function herkunft(tulpe: Tulip) {
  return tulpe.src ? stelleAus(tulpe.src) : null
}

/**
 * Die Richtungswahl einer Form, als Knopfreihe statt als Auswahlliste.
 *
 * Knöpfe, weil sie mit dem Daumen zu treffen sind und weil höchstens vier zur
 * Wahl stehen - eine Auswahlliste wäre hier zwei Handgriffe für etwas, das
 * einer ist.
 */
function Richtungswahl({ tulpe, onChange }: { tulpe: Tulip; onChange: (turn: number) => void }) {
  const info = tulipShapeInfo(tulpe.shape)
  if (info.directions.length === 0) return null

  const gewaehlt = exitDirection(tulpe.shape, tulpe.turn)

  return (
    <div className="feld tulpen-richtung">
      <span>{info.group === 'knoten' ? 'Wohin' : 'Auf welcher Seite'}</span>
      <div className="knopfreihe">
        {info.directions.map((richtung) => (
          <button
            key={richtung}
            type="button"
            className={`knopf ${richtung === gewaehlt ? '' : 'still'}`}
            aria-pressed={richtung === gewaehlt}
            onClick={() => onChange(richtung)}
          >
            {directionLabel(richtung)}
          </button>
        ))}
      </div>
    </div>
  )
}
